import ConvertIds from '@atlas/atlas-convert-ids';
import { SnapPointBaseElement, SnapPointSelectionObject } from './SnapPointBaseElement.js';
import { generateSelObj, getPointInProtoSpace, mouseMoveCallback } from './atlasSelectionUtils';
import markerUtils from './markerUtils';

let counter = 0;
let controlManager = null;
let viewer = null;
let PLMVisWeb = null;
/** @type {HTMLElement} */
let svgOverlay = null;
let activeEdges = new Map();
let SPLM = null;
let sendApi = null;
/** @type {import('./AtlasSelection.js').AtlasSelection} */
let atlasSelection = null;
let apiId = 0;
let sgoMgr = null;
let sgoUtils = null;
//TODO: Deactivating/deleting element should remove listener from ODD

let preselId = null;
let highlightLoc = null;
let markRadius = 1;

let camChange = false;

const getPointOnFaceRule = (facePointObj) => {
    let splmApiCached = SPLM.api;
    SPLM.api = [];
    let mouseCoordx = facePointObj.xPx;
    let mouseCoordy = facePointObj.yPx;
    let location = facePointObj.location;

    let theSession = new SPLM.Session();
    let workPart = theSession.Parts.Work;

    const faceProtoAtlasId = ConvertIds.toPrototype(facePointObj.parentId);
    let faceObj = new SPLM.Face(faceProtoAtlasId);

    let result = {};
    let refPoint = { x: location.x, y: location.y, z: location.z };
    if (!facePointObj.isPrototypeSpace) {
        // Make sure we do the transformation only once
        refPoint = getPointInProtoSpace(viewer, PLMVisWeb, facePointObj.parentPsId, location);
        facePointObj.isPrototypeSpace = true;
        facePointObj.location = refPoint; // Save the point on prototype
    }
    result.point = workPart.Points.CreateClosestPointToFace(faceObj, refPoint, SPLM.SmartObject.UpdateOption.WithinModeling);

    result.api = SPLM.api;

    SPLM.api = splmApiCached;

    return result;
};

let initialized = false;

/**
 * SnapOnFaceElement constructor.  This element allows snap point on face selection.  It behaves like any
 * other SelectionElement in that it generates selection objects that are added onto the selection list.  These
 * selection objects contain the precise point xyz in the result member, and the SDK objects necessary for
 * Atlas to generate the selected point in the recipe member.
 */
export class SnapOnFaceElement extends SnapPointBaseElement {
    /**
     *
     * @param {object}   controlManagerInp - The PLMVisWeb control manager.  It is used to access the viewer
     *                                       dimensions, and to gain access to the Viewer object in use.
     * @param {object}   PLMVisWebInp      - The PLMVisWeb module itself
     * @param {HTMLElement} svgOverlayInp   - The SVG DOM element that overlays the PLMVisWeb canvas.  This element
     *                                       must be supplied by the application.  Galaxy already provides and maintains
     *                                       this element. Due to performance considerations, this element uses SVG for
     *                                       prehighlighing of the snap point locations.
     * @param {object}   SPLMInp           - The SPLM SDK object, which must have the point construction APIs defined.
     * @param {object}   Sphere            - An object that provides sphere (sgo) constructor
     * @param {object}   sgoApi            - An object that provides api for sgo, i.e. mouse over, hightlight.
     * @param {Function} sendApiInp        - A function that will send an API message to Atlas asynchronously. This function is
     *                                       expected to take one input object { API: xxx, ApiDescription: yyy }:
     *                                            1) xxx: the array of SPLM SDK instructions (SPLM.api),
     *                                            2) yyy: the description for the API which is used as API message Id.
     *                                       It should return a promise that resolves with the api result from Atlas.
     *                                       Galaxy already provides this function.
     * @param {import('./AtlasSelection.js').AtlasSelection} atlasSelInp
     */
    constructor(controlManagerInp, PLMVisWebInp, svgOverlayInp, SPLMInp, Sphere, sgoApi, sendApiInp, atlasSelInp) {
        super(controlManagerInp, PLMVisWebInp, svgOverlayInp, SPLMInp, Sphere, sendApiInp, atlasSelInp);
        controlManager = controlManagerInp;
        viewer = controlManagerInp.viewer;
        PLMVisWeb = PLMVisWebInp;
        svgOverlay = svgOverlayInp;
        SPLM = SPLMInp;
        sendApi = sendApiInp;
        atlasSelection = atlasSelInp;
        markRadius = this._markRadius;
        sgoMgr = super.sgoMgr;
        sgoUtils = sgoApi;
    }
    get active() {
        return super.active;
    }
    set active(newActive) {
        super.active = newActive;
    }
    /**
     * @param {MouseEvent} e
     * @param {any} curObjects
     */
    async onMouseMove(e, curObjects) {
        let mouseMoveParams = {
            eventData: e,
            curObjects: curObjects,
            viewer: viewer,
            sgoMgr: sgoMgr,
            svgOverlay: svgOverlay,
            snapType: 'SnapOnFace',
            displayPoint: displayPoint,
            markRadius: markRadius,
            preselId: preselId,
            highlighLoc: highlightLoc
        };
        let retObj = await mouseMoveCallback(mouseMoveParams, sgoUtils);
        preselId = mouseMoveParams.preselId;
        highlightLoc = mouseMoveParams.highlightLoc;
        return retObj;
    }

    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        const nameArr = psId.split(':');
        const modelId = nameArr[0];
        // if we're over a location, return its rule
        if (highlightLoc) {
            const parentPsId = ConvertIds.toPsId(modelId, highlightLoc.parentId);
            if (psId === parentPsId) {
                let loc = highlightLoc;
                // loc.parentId will only be the prototype.  Need to ensure psId is using
                // same prototype, then create new id using assembly context of psId.
                return generateSelObj(getPointOnFaceRule, loc, psId, viewer, 'SnapOnFace', counter, this, SnapPointSelectionObject);
            }
        }
    }

    /**
     * @param {SnapPointSelectionObject} selectionObject
     * @param {boolean} select
     * @param {number} reason
     */
    select(selectionObject, select, reason) {
        super.select(selectionObject, select, reason);
        if (select) {
            counter++;
        }
        return [];
    }
    getPickingMask() {
        return PLMVisWeb.PickingMode.FACE;
    }
    getDisplayName() {
        return 'SnapOnFace';
    }

    /**
     * @param {SnapPointSelectionObject} candidateObject
     * @param {boolean} isHighlight
     */
    highlight(candidateObject, isHighlight) {
        let parentPsId = candidateObject.psIds[0];
        let snapPointData = candidateObject.result;

        if (!snapPointData.sgo || (snapPointData.sgo && !snapPointData.sgo.isVisible())) {
            // We are not on an already selected point (SGO)
            let mouseLoc = { x: snapPointData.xPx, y: snapPointData.yPx };

            if (!snapPointData.marker) {
                let pointData = { point: snapPointData };
                displayPoint(parentPsId, pointData, mouseLoc);
            }
            markerUtils.highlightMarker(snapPointData.marker);
            candidateObject.marker = snapPointData.marker;
        } else {
            // We are on an already selected point (SGO)
            sgoUtils.highlightSGO(snapPointData.sgo, isHighlight);
        }

        return undefined;
    }
}

/**
 *
 * @param {string} psId
 * @param {object} pointData
 * @param {object} mouseLoc
 */
const displayPoint = async function (psId, pointData, mouseLoc) {
    let loc = undefined;
    let object = viewer._modelObject.getObjectByPsId(psId);

    if (atlasSelection) {
        // if it clears preselection add it
        let selCand = generateSelObj(getPointOnFaceRule, pointData.point, psId, viewer, 'SnapOnFace', counter, undefined, SnapPointSelectionObject);
        let selEvent = await atlasSelection.processPreSelection([selCand]);
        if (selEvent?.accepted && selEvent?.addCandidates?.length > 0) {
            loc = selEvent.addCandidates[0].result;
        }
    } else {
        loc = pointData.point;
    }
    if (loc) {
        markerUtils.clearMarkersOfLocType(svgOverlay, 'SnapOnFace');
        markerUtils.displayMarker(PLMVisWeb, viewer, svgOverlay, markRadius, psId, loc, undefined);
    }
};

export default SnapOnFaceElement;
